package com.exura.features.plandefinition


import com.exura.features.localization.domain.model.BasicLangSet
import com.exura.features.localization.domain.model.Language
import com.exura.features.localization.domain.model.Localization
import com.exura.features.plandefinition.domain.model.series.PartOfDay

interface PlanLang {
    val series: Series
    val metadata: Metadata
    val distribution: Distribution

    val plan: String
    val plans: String
    val treatmentPlans: String
    fun plans(number: Int): String
    fun plansCount(number: Int): String = "$number ${plans(number)}" // If some language specifies different order, they can override

    val day: String
    val days: String
    fun days(number: Int): String
    fun daysCount(number: Int): String = "$number ${days(number)}" // If some language specifies different order, they can override

    val minute: String
    val minutes: String
    fun minutes(number: Int): String
    fun minutesCount(number: Int): String = "$number ${minutes(number)}" // If some language specifies different order, they can override

    fun partOfDayToString(partOfDay: PartOfDay): String

    val title: String

    val noPlansAvailable: String

    interface Series {
        val task: String
        val tasks: String
        fun tasks(number: Int): String
        fun tasksCount(number: Int): String = "$number ${tasks(number)}"

        val persistent: String
        val persistents: String
        fun persistents(number: Int): String
        fun persistentsCount(number: Int): String = "$number ${persistents(number)}"

        val floating: String
        val floatings: String
        fun floatings(number: Int): String
        fun floatingsCount(number: Int): String = "$number ${floatings(number)}"

        val question: String
        val questions: String
        fun questions(number: Int): String
        fun questionsCount(number: Int): String = "$number ${questions(number)}"

        val communique: String
        val communiques: String
        fun communiques(number: Int): String
        fun communiquesCount(number: Int): String = "$number ${communiques(number)}"
    }

    interface Metadata {
        val duration: String
        val modification: String
        val lastModification: String
        val planSummary: String
    }

    interface Distribution {
        val planDistribution: String
    }

}

data object PlanPolish : PlanLang {
    override val series = Series
    override val metadata = Metadata
    override val distribution = Distribution

    override val plan: String = "Plan"
    override val plans: String = "Plany"
    override val treatmentPlans: String = "Plany Leczenia"
    override fun plans(number: Int): String = when {
        number == 1 -> "Plan"
        number.mod(10) in 2..4 -> "Plany"
        else -> "Planów"
    }

    override val day: String = "dzień"
    override val days: String = "dni"
    override fun days(number: Int): String = when {
        number == 1 -> "dzień"
        number in 2..4 -> "dni"
        else -> "dni"
    }

    override val minute: String = "minuta"
    override val minutes: String = "minuty"
    override fun minutes(number: Int): String = when {
        number == 1 -> "minuta"
        number in 11..14 -> "minut"
        number.mod(10) in 2..4 -> "minuty"
        else -> "minut"
    }

    override fun partOfDayToString(partOfDay: PartOfDay): String = when (partOfDay) {
        PartOfDay.MORNING -> "Rano"
        PartOfDay.NOON -> "Południe"
        PartOfDay.AFTERNOON -> "Popołudnie"
        PartOfDay.EVENING -> "Wieczór"
        PartOfDay.NIGHT -> "Noc"
        PartOfDay.TODAY -> "Dziś"
    }

    override val title: String = "Tytuł"

    override val noPlansAvailable = "Brak planów"

    data object Series : PlanLang.Series {
        override val task: String = "Zadanie"
        override val tasks: String = "Zadania"
        override fun tasks(number: Int): String = when {
            number == 1 -> "zadanie"
            number in 11..14 -> "zadań"
            number.mod(10) in 2..4 -> "zadania"
            else -> "zadań"
        }

        override val persistent: String = "Trwałe"
        override val persistents: String = "Trwałe"
        override fun persistents(number: Int): String = when {
            number == 1 -> "trwałe"
            number in 11..14 -> "trwałych"
            number.mod(10) in 2..4 -> "trwałe"
            else -> "trwałych"
        }

        override val floating: String = "Dynamiczne"
        override val floatings: String = "Dynamiczne"
        override fun floatings(number: Int): String = when {
            number == 1 -> "dynamiczne"
            number in 11..14 -> "dynamicznych"
            number.mod(10) in 2..4 -> "dynamiczne"
            else -> "dynamicznych"
        }

        override val question: String = "Pytanie"
        override val questions: String = "Pytania"
        override fun questions(number: Int): String = when {
            number == 1 -> "pytanie"
            number in 11..14 -> "pytań"
            number.mod(10) in 2..4 -> "pytania"
            else -> "pytań"
        }

        override val communique: String = "komunikat"
        override val communiques: String = "komunikaty"
        override fun communiques(number: Int): String = when {
            number == 1 -> "komunikat"
            number in 11..14 -> "komunikatów"
            number.mod(10) in 2..4 -> "komunikaty"
            else -> "komunikatów"
        }
    }

    data object Metadata : PlanLang.Metadata {
        override val duration: String = "czas trwania"
        override val modification: String = "Modyfikacja"
        override val lastModification: String = "Ostatnia modyfikacja"
        override val planSummary: String = "Podsumowanie planu"
    }

    data object Distribution : PlanLang.Distribution {
        override val planDistribution: String = "Wyślij plan"
    }
}

data object PlanEnglish : PlanLang {
    override val series = Series
    override val metadata = Metadata
    override val distribution = Distribution

    override val title: String = "Title"
    override val noPlansAvailable = "No plans available"

    override val plan: String = "Plan"
    override val plans: String = "Plans"
    override val treatmentPlans: String = "Treatment Plans"
    override fun plans(number: Int): String = when {
        number == 1 -> "Plan"
        else -> "Plans"
    }

    override val day: String = "Day"
    override val days: String = "Days"
    override fun days(number: Int): String = when {
        number == 1 -> "Day"
        else -> "Days"
    }

    override val minute: String = "minute"
    override val minutes: String = "minutes"
    override fun minutes(number: Int): String = when {
        number == 1 -> "minute"
        else -> "minutes"
    }

    override fun partOfDayToString(partOfDay: PartOfDay): String = when (partOfDay) {
        PartOfDay.MORNING -> "Morning"
        PartOfDay.NOON -> "Noon"
        PartOfDay.AFTERNOON -> "Afternoon"
        PartOfDay.EVENING -> "Evening"
        PartOfDay.NIGHT -> "Night"
        else -> "Today"
    }

    data object Series : PlanLang.Series {
        override val task: String = "Task"
        override fun tasks(number: Int): String = when {
            number == 1 -> "Task"
            else -> "Tasks"
        }

        override val tasks: String = "Tasks"

        override val persistent: String = "Persistent"
        override val persistents: String = "Persistents"
        override fun persistents(number: Int): String = when {
            number == 1 -> "Persistent"
            else -> "Persistents"
        }

        override val floating: String = "Floating"
        override val floatings: String = "Floatings"
        override fun floatings(number: Int): String = when {
            number == 1 -> "Floating"
            else -> "Floatings"
        }

        override val question: String = "Question"
        override val questions: String = "Questions"
        override fun questions(number: Int): String = when {
            number == 1 -> "Question"
            else -> "Questions"
        }

        override val communique: String = "Message"
        override val communiques: String = "Messages"
        override fun communiques(number: Int): String = when {
            number == 1 -> "Message"
            else -> "Messages"
        }
    }

    data object Metadata : PlanLang.Metadata {
        override val duration: String = "Duration"
        override val modification: String = "Modification"
        override val lastModification: String = "Last modification"
        override val planSummary: String = "Plan Summary"
    }

    data object Distribution : PlanLang.Distribution {
        override val planDistribution: String = "Plan Distribution"
    }
}

private val PlanLangSet = BasicLangSet<PlanLang>(
    mapOf(
        Language.POLISH to PlanPolish,
        Language.ENGLISH to PlanEnglish,
    )
)

val planLocalization = Localization<PlanLang>(PlanLangSet)
